import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/i18n/LanguageContext';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { EditIcon } from '@/components/icons/EditIcon';
import { useAuth, useSubscription } from '@/utils/AuthContext';
import { projectsApi, Project } from '@/utils/api-pb';
import { CreateProjectModal } from '@/components/projects/CreateProjectModal';
import { EditProjectModal } from '@/components/projects/EditProjectModal';
import LocalizedLink from '@/components/shared/LocalizedLink';

// Шаги с навигацией (пункты 2-6 кликабельны)
const stepKeys = [
  { number: 1, key: 'steps.1', href: '' },
  { number: 2, key: 'steps.2', href: '/market-analysis' },
  { number: 3, key: 'steps.3', href: '/personas' },
  { number: 4, key: 'steps.4', href: '/research' },
  { number: 5, key: 'steps.5', href: '/positioning' },
  { number: 6, key: 'steps.6', href: '/mvp-plan' },
];

// Лимиты проектов по подпискам
const PROJECT_LIMITS: Record<string, number> = {
  free: 1,
  student: 1,
  basic: 1,
  full: 5,
  // Legacy
  creator: 3,
  top_creator: Infinity,
};

export default function DashboardPage() {
  const { user } = useAuth();
  const { tier: subscriptionTier, isFree, isUnlimited } = useSubscription();
  const location = useLocation();
  const { t } = useTranslation('dashboard');
  const { lang } = useLanguage();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [hasShownAutoModal, setHasShownAutoModal] = useState(false);

  const userName = user?.name || user?.email?.split('@')[0] || t('defaultUserName');

  // Лимит проектов для текущей подписки + докупленные проекты
  const extraProjects = (user as any)?.extra_projects || 0;
  const baseLimit = isUnlimited ? Infinity : (PROJECT_LIMITS[subscriptionTier] ?? 1);
  const projectLimit = baseLimit === Infinity ? Infinity : baseLimit + extraProjects;
  const canAddProject = projects.length < projectLimit;

  useEffect(() => {
    loadProjects();
  }, []);

  // Автоматически показываем модалку для новых пользователей (без проектов)
  useEffect(() => {
    if (!isLoading && projects.length === 0 && !hasShownAutoModal) {
      setShowCreateModal(true);
      setHasShownAutoModal(true);
    }
  }, [isLoading, projects.length, hasShownAutoModal]);

  // Открываем модалку создания проекта при навигации с state
  useEffect(() => {
    if ((location.state as { openCreateProject?: boolean })?.openCreateProject) {
      setShowCreateModal(true);
      // Очищаем state, чтобы модалка не открывалась повторно при обновлении
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const loadProjects = async () => {
    try {
      const result = await projectsApi.list();
      setProjects(result);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Обработчик успешного создания проекта
  const handleProjectCreated = () => {
    loadProjects();
  };

  // Обработчик клика на проект - открываем модалку редактирования
  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    setShowEditModal(true);
  };

  // Обработчик успешного обновления проекта
  const handleProjectUpdated = () => {
    loadProjects();
  };

  // Обработчик удаления проекта
  const handleProjectDeleted = () => {
    loadProjects();
  };

  // Текст для неактивной кнопки добавления проекта
  const getDisabledButtonText = () => {
    if (isFree) {
      return t('projects.addNewPaid');
    }
    if (!canAddProject) {
      return t('projects.limitReached');
    }
    return t('projects.addNew');
  };

  return (
    <>
      {/* Приветствие - Manrope Bold 32px/40px */}
      <h1 className="font-manrope font-bold text-[24px] leading-[32px] md:text-[32px] md:leading-[40px] text-[#0d0d0d] mb-[16px] md:mb-[32px]">
        {t('greeting', { name: userName })}
      </h1>

      <div className="flex flex-col md:flex-row gap-[16px]">
        {/* Левая колонка - растягивается */}
        <div className="flex flex-col gap-[16px] flex-1">
          {/* С чего начать */}
          <Card className="bg-white border-0 rounded-[16px] shadow-none">
            <CardContent className="p-[24px] flex flex-col gap-[16px]">
              {/* Заголовок + описание */}
              <div className="flex flex-col gap-[8px]">
                <h2 className="font-manrope font-semibold text-[20px] leading-[28px] text-[#0d0d0d]">
                  {t('gettingStarted.title')}
                </h2>
                {/* Описание - Segoe UI Regular 14px/20px, цвет #565858 */}
                <div className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  <p className="mb-[8px]">
                    {t('gettingStarted.description1')}
                  </p>
                  <p>{t('gettingStarted.description2')}</p>
                </div>
              </div>
              {/* Список шагов - Segoe UI Regular 16px/24px, цвет #0d0d0d */}
              <ol className="font-segoe list-decimal pl-[20px] space-y-[8px] text-[16px] leading-[24px] text-[#0d0d0d]">
                {stepKeys.map((step) => (
                  <li key={step.number}>
                    {step.href ? (
                      <LocalizedLink to={step.href} className="text-[#0d0d0d] hover:text-[#2197f1] transition-colors underline decoration-transparent hover:decoration-[#2197f1]">
                        {t(step.key)}
                      </LocalizedLink>
                    ) : (
                      t(step.key)
                    )}
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>

          {/* Мои проекты */}
          <Card className="bg-white border-0 rounded-[16px] shadow-none">
            <CardContent className="p-[24px] flex flex-col gap-[16px]">
              <h2 className="font-manrope font-semibold text-[20px] leading-[28px] text-[#0d0d0d]">
                {t('projects.title')}
              </h2>

              {isLoading ? (
                <div className="flex flex-col gap-[8px]">
                  <div className="h-[56px] bg-[#f8f8f8] animate-pulse rounded-[8px]" />
                </div>
              ) : projects.length > 0 ? (
                <div className="flex flex-col gap-[8px]">
                  {projects.map((project, index) => {
                    // На бесплатном тарифе проекты кроме первого - disabled
                    const isDisabled = isFree && index > 0;

                    return (
                      <div
                        key={project.id}
                        onClick={() => !isDisabled && handleProjectClick(project)}
                        className={`flex items-center gap-[16px] p-[16px] rounded-[8px] transition-colors ${
                          isDisabled
                            ? 'bg-[#f8f8f8] opacity-60 cursor-not-allowed'
                            : 'bg-[#f8f8f8] hover:bg-[#eeeeee] cursor-pointer'
                        }`}
                      >
                        {/* Название проекта - Segoe UI Regular 16px/24px */}
                        <span className={`font-segoe flex-1 text-[16px] leading-[24px] ${isDisabled ? 'text-[#565858]' : 'text-[#0d0d0d]'}`}>
                          {project.name}
                        </span>
                        {/* Иконка edit 24x24 */}
                        <EditIcon className="w-[24px] h-[24px]" />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="font-segoe text-[16px] leading-[24px] text-[#565858] py-[16px] text-center">
                  {t('projects.empty')}
                </p>
              )}

              {/* Кнопка добавления проекта */}
              {canAddProject ? (
                <Button
                  className="w-full bg-[rgba(33,151,241,0.2)] hover:bg-[#2197f1] hover:text-white border-0 text-[#2197f1] rounded-[16px] px-[24px] py-[8px] h-auto font-segoe font-semibold text-[14px] leading-[20px] tracking-[0.1px]"
                  variant="ghost"
                  onClick={() => setShowCreateModal(true)}
                >
                  {t('projects.addNew')}
                </Button>
              ) : (
                <Button
                  className="w-full bg-[#eeeeee] hover:bg-[#eeeeee] border-0 text-[#565858] rounded-[16px] px-[24px] py-[8px] h-auto cursor-not-allowed font-segoe font-semibold text-[14px] leading-[20px] tracking-[0.1px]"
                  variant="ghost"
                  disabled
                >
                  {getDisabledButtonText()}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Правая колонка - растягивается */}
        <div className="flex flex-col gap-[16px] flex-1">
          {/* Карточка "Вы на бесплатном тарифе" - только для бесплатной подписки */}
          {isFree && (
            <Card className="bg-white border border-[#ff9196] rounded-[16px] shadow-none">
              <CardContent className="p-[24px] flex flex-col gap-[16px]">
                <div className="flex flex-col gap-[8px]">
                  {/* Иконка warning 24x24 */}
                  <AlertCircle className="w-[24px] h-[24px] text-[#565858]" />
                  {/* Заголовок - Manrope SemiBold 20px/28px */}
                  <h3 className="font-manrope font-semibold text-[20px] leading-[28px] text-[#0d0d0d]">
                    {t('freePlan.title')}
                  </h3>
                  {/* Описание - Segoe UI Regular 14px/20px */}
                  <p className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                    {t('freePlan.description')}
                  </p>
                </div>
                {/* Кнопка - Segoe UI Semibold 16px/24px, py-12 */}
                <Button
                  className="w-full bg-[#2197f1] hover:bg-[#1a7fd1] text-white rounded-[16px] px-[24px] py-[12px] h-auto font-segoe font-semibold text-[16px] leading-[24px] tracking-[0.1px]"
                  asChild
                >
                  <LocalizedLink to="/subscription">{t('freePlan.choosePlan')}</LocalizedLink>
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Предложить улучшение */}
          <Card className="bg-white border-0 rounded-[16px] shadow-none">
            <CardContent className="p-[24px] flex flex-col gap-[16px]">
              {/* Текст в 2 строки - Manrope SemiBold 20px/28px */}
              <div className="font-manrope font-semibold text-[20px] leading-[28px] text-[#0d0d0d]">
                <p>{t('feedback.line1')}</p>
                <p>{t('feedback.line2')}</p>
              </div>
              {/* Кнопка маленькая - py-8, 14px текст */}
              <Button
                variant="ghost"
                className="w-full bg-[rgba(33,151,241,0.2)] hover:bg-[#2197f1] hover:text-white border-0 text-[#2197f1] rounded-[16px] px-[24px] py-[8px] h-auto font-segoe font-semibold text-[14px] leading-[20px] tracking-[0.1px]"
                asChild
              >
                <a href={lang === 'en' ? "https://create-products.com/en/testimonial" : "https://create-products.com/testimonial"} target="_blank" rel="noopener noreferrer">
                  {t('feedback.button')}
                </a>
              </Button>
            </CardContent>
          </Card>

          {/* Справка */}
          <Card className="bg-white border-0 rounded-[16px] shadow-none">
            <CardContent className="p-[24px]">
              {/* Текст в 2 строки - Manrope SemiBold 20px/28px */}
              <div className="font-manrope font-semibold text-[20px] leading-[28px]">
                <p className="text-[#0d0d0d]">{t('help.line1')}</p>
                <LocalizedLink to="/help" className="text-[#2197f1] underline decoration-solid">
                  {t('help.link')}
                </LocalizedLink>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Модалка создания проекта */}
      <CreateProjectModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onSuccess={handleProjectCreated}
      />

      {/* Модалка редактирования проекта */}
      <EditProjectModal
        project={selectedProject}
        open={showEditModal}
        onOpenChange={setShowEditModal}
        onSuccess={handleProjectUpdated}
        onDelete={handleProjectDeleted}
      />
    </>
  );
}
